import React, { useState } from 'react';
import { Card, Form, Input, Button, Space, Typography, Alert } from 'antd';
import { SaveOutlined } from '@ant-design/icons';
import LocationSelector from '../components/LocationSelector';
import GPSCoordinates from '../components/GPSCoordinates';
import { useTheme } from '@/components/common/ThemeProvider';
import { useLocationValidation } from '../hooks/useLocationValidation';

const { Title } = Typography;

interface CustomerLocationFormData {
    customerId: number;
    addressType: string;
    locationSelections: any;
    gpsCoordinates: any;
    houseNumber: string;
    postalCode: string;
}

const CustomerLocationExample: React.FC = () => {
    const { dark } = useTheme();
    const [form] = Form.useForm();
    const [formData, setFormData] = useState<CustomerLocationFormData>({
        customerId: 1,
        addressType: 'PHYSICAL',
        locationSelections: {},
        gpsCoordinates: {},
        houseNumber: '',
        postalCode: ''
    });
    const [validationResult, setValidationResult] = useState<any>(null);

    const { validateLocationForConfig, isValidating } = useLocationValidation();

    const handleLocationChange = (selections: any) => {
        setFormData(prev => ({
            ...prev,
            locationSelections: selections
        }));
    };

    const handleGPSChange = (coordinates: any) => {
        setFormData(prev => ({
            ...prev,
            gpsCoordinates: coordinates
        }));
    };

    const handleValidate = async () => {
        const validationRequest = {
            zoneId: formData.locationSelections.zone?.id,
            regionId: formData.locationSelections.region?.id,
            districtId: formData.locationSelections.district?.id,
            wardId: formData.locationSelections.ward?.id,
            streetId: formData.locationSelections.street?.id,
            houseNumber: formData.houseNumber,
            postalCode: formData.postalCode,
            latitude: formData.gpsCoordinates.latitude,
            longitude: formData.gpsCoordinates.longitude,
            gpsAccuracy: formData.gpsCoordinates.accuracy
        };

        const result = await validateLocationForConfig(validationRequest, 'CUSTOMER_PHYSICAL_ADDRESS');
        setValidationResult(result);
    };

    const handleSave = async () => {
        try {
            const values = await form.validateFields();
            // Save customer location logic here
            console.log('Saving customer location:', {
                ...formData,
                ...values
            });
        } catch (error) {
            console.error('Form validation failed:', error);
        }
    };

    return (
        <div className="space-y-6">
            <Card
                className={dark ? 'bg-gray-800 text-white' : ''}
                title={
                    <Title level={4} className={dark ? 'text-white mb-0' : 'mb-0'}>
                        Customer Location Example
                    </Title>
                }
            >
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div className="space-y-6">
                        <LocationSelector
                            title="Customer Physical Address"
                            configName="CUSTOMER_PHYSICAL_ADDRESS"
                            onLocationSelected={handleLocationChange}
                            required={{
                                zone: true,
                                region: true,
                                district: true,
                                ward: true
                            }}
                        />

                        <GPSCoordinates
                            required={false}
                            accuracyThreshold={50}
                            onCoordinatesChange={handleGPSChange}
                        />
                    </div>

                    <div className="space-y-6">
                        <Card
                            title="Additional Details"
                            className={dark ? 'bg-gray-700' : ''}
                        >
                            <Form
                                form={form}
                                layout="vertical"
                            >
                                <Form.Item
                                    name="houseNumber"
                                    label="House Number"
                                    rules={[{ required: true, message: 'Please enter house number' }]}
                                >
                                    <Input
                                        placeholder="e.g., Plot 123, Block A"
                                        onChange={(e) => setFormData(prev => ({
                                            ...prev,
                                            houseNumber: e.target.value
                                        }))}
                                    />
                                </Form.Item>

                                <Form.Item
                                    name="postalCode"
                                    label="Postal Code"
                                >
                                    <Input
                                        placeholder="e.g., 12345"
                                        onChange={(e) => setFormData(prev => ({
                                            ...prev,
                                            postalCode: e.target.value
                                        }))}
                                    />
                                </Form.Item>
                            </Form>
                        </Card>

                        {validationResult && (
                            <Alert
                                message="Validation Result"
                                description={
                                    validationResult.valid
                                        ? 'Location data is valid'
                                        : `Validation errors: ${validationResult.errors?.join(', ')}`
                                }
                                type={validationResult.valid ? 'success' : 'error'}
                                showIcon
                            />
                        )}

                        <Space>
                            <Button
                                onClick={handleValidate}
                                loading={isValidating}
                            >
                                Validate
                            </Button>
                            <Button
                                type="primary"
                                icon={<SaveOutlined />}
                                onClick={handleSave}
                                disabled={validationResult && !validationResult.valid}
                            >
                                Save Location
                            </Button>
                        </Space>
                    </div>
                </div>
            </Card>
        </div>
    );
};

export default CustomerLocationExample;